import React, { useEffect, useRef, useState, useMemo } from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import {
  CONFERENCE_ROLE_ORGANIZER,
  CONFERENCE_ROLE_PRESENTER,
  CONFERENCE_CHAT_AUDIENCE_PUBLIC,
  CONFERENCE_CHAT_AUDIENCE_PRIVATE,
} from "../../Constants/constantValues";
import CallChatHeader from "./CallChatHeader";
import CallChatMessagesList from "./CallChatMessagesList";
import CallChatInput from "./CallChatInput";
import { useAuthService } from "../../Context/AuthContext";
import { useConversationService } from "../../Context/ConversationsContext";
import { useNotify } from "../../Helpers/Notifications";
import SpinnerComponent from "../GenericComponents/SpinnerComponent";
import { 
  checkCallChatConversation, 
  createCallChatConversation 
} from "../../API/calls/callChatService";
import { useWebSocketDataService } from "../../Context/WebsocketContext";
import { ReactComponent as NoMessageIcon } from "../../Assets/iconsv2/controlButtons/NoMessage.svg";
import { mapEmoji } from "../../Constants/emojiList";
import copy from "copy-to-clipboard";
import { useContextMenu } from "react-contexify";
import RightClickMenu from "../Chat/RightClickMenu";
import EditMessageContainer from "../Chat/EditMessageContainer";
import ReplyMessageContainer from "../Chat/ReplyMessageContainer";
import { findTranslatedTextForUser } from "../../Helpers/chatHelper/ChatHelper";
import FullscreenImage from "../Chat/FullscreenImage";

const CallChatPanel = ({
  callInfo,
  onClose,
  activeCallRef,
  existingConversation,
  onConversationCreated,
  onMessagesRead,
  fullWidth = false,
}) => {
  const { t } = useTranslate();
  const { user } = useAuthService();
  const { notifyError, notifySuccess, notifyCallChatMessage } = useNotify();
  
  const [callChatConversation, setCallChatConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isCheckingConversation, setIsCheckingConversation] = useState(!existingConversation);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const { add_observer, remove_observer } = useWebSocketDataService();

  const mediaInputFileRef = useRef(null);
  const fileInputRef = useRef(null);
  const [mediaInput, setMediaInput] = useState(null);
  const [fileInput, setFileInput] = useState(null);
  const messageInputRef = useRef(null);
  const [selectedMessageForContextMenu, setSelectedMessageForContextMenu] = useState(null);
  const [showEditMessageModal, setShowEditMessageModal] = useState(false);
  const [showReplyMessageModal, setShowReplyMessageModal] = useState(false);
  const [messageToEdit, setMessageToEdit] = useState(null);
  const [messageToReply, setMessageToReply] = useState(null);
  const [showFullScreenImg, setShowFullScreenImg] = useState(false);
  const [fullScreenImg, setFullScreenImg] = useState(null);
  const MENU_ID = "call-chat-menu-id";

  const { show } = useContextMenu({
    id: MENU_ID,
  });
  
  const {
    getMessageForConversation,
    addMessageForConversation,
    markMessagesAsRead,
    reactToMessageForConversation,
    removeOwnMessageReaction,
    deleteMessageForConversation,
    editMessageForConversation,
    replyMessage,
    setConversations,
  } = useConversationService();

  const messagesEndRef = useRef(null);
  const conversationIdRef = useRef(existingConversation?.id || null);
  const isLoadingRef = useRef(false);

  const isConferenceCall = !!callInfo?.conference_id;
  const isPanelViewer =
    isConferenceCall &&
    [CONFERENCE_ROLE_ORGANIZER, CONFERENCE_ROLE_PRESENTER].includes(
      callInfo?.conference_role
    );

  const [chatRecipient, setChatRecipient] = useState(null);

  const recipientOptions = useMemo(() => {
    if (!isPanelViewer) return [];
    const byId = new Map();
    messages.forEach((m) => {
      if (m?.conference_audience !== CONFERENCE_CHAT_AUDIENCE_PRIVATE) return;
      const id = m.conference_recipient_user_id;
      if (id == null || byId.has(id)) return;
      byId.set(id, { id, name: m.conference_recipient_name || t("general.unknown_user") });
    });
    return Array.from(byId.values());
  }, [isPanelViewer, messages, t]);

  useEffect(() => {
    if (chatRecipient && !recipientOptions.some((o) => o.id === chatRecipient.id)) {
      setChatRecipient(null);
    }
  }, [recipientOptions, chatRecipient]);

  const [leftPanelView, setLeftPanelView] = useState(null);
  const [unreadCallChatCount, setUnreadCallChatCount] = useState(0);
  const seenMessageIds = useRef(new Set());
  const markedAsReadIds = useRef(new Set());
  const leftPanelViewRef = useRef(leftPanelView);
  const notifyCallChatMessageRef = useRef(notifyCallChatMessage);
  const tRef = useRef(t);
  
  const initialLoadTimestampRef = useRef(null);
  const chatOpenedTimestampRef = useRef(Date.now());

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "end"
      });
    }
  };

  useEffect(() => {
    if (!existingConversation && !callChatConversation && callInfo?.id) {
      setIsCheckingConversation(true);

      const isConferenceCall = !!callInfo.conference_id;
      
      checkCallChatConversation(
        callInfo.id,
        (response) => {
          const conversation = response.data.conversation;
          
          if (conversation?.id) {
            setCallChatConversation(conversation);
            conversationIdRef.current = conversation.id;
            
            if (onConversationCreated) {
              onConversationCreated(conversation);
            }
            
            loadMessagesForConversation(conversation.id);
          }

          setIsCheckingConversation(false);
        },
        (error) => {
          setIsCheckingConversation(false);
        },
      isConferenceCall
      );
    }
  }, [callInfo?.id]);

  useEffect(() => {    

    conversationIdRef.current = existingConversation?.id || callChatConversation?.id || null;
    const conversation = existingConversation || callChatConversation;
    if (!conversation?.id) {
      setIsCheckingConversation(false);
      setCallChatConversation(null);
      setMessages([]);
      setIsLoadingMessages(false);
      return;
    }

    if (messages.length > 0 && messages[0]?.conversation_id !== conversation.id) {
      setMessages([]);
    }

    if (callChatConversation?.id !== conversation.id) {
      setCallChatConversation(conversation);
    }
    
    setIsCheckingConversation(false);
    
    const needsToLoadMessages = 
      !isLoadingMessages && (
        messages.length === 0 || 
        (messages.length > 0 && messages[0]?.conversation_id !== conversation.id)
      );
    
    if (needsToLoadMessages) {
      loadMessagesForConversation(conversation.id);
      
      if (onMessagesRead) {
        onMessagesRead();
      }
    }
  }, [existingConversation?.id, callChatConversation?.id]);

  useEffect(() => {
    if (!existingConversation?.id) return;

    const isNewConversation = existingConversation.id !== conversationIdRef.current;
    const hasWrongMessages = messages.length > 0 && messages[0]?.conversation_id !== existingConversation.id;

    if (isNewConversation || hasWrongMessages) {
      conversationIdRef.current = existingConversation.id;
      setCallChatConversation(existingConversation);
      setMessages([]);

      loadMessagesForConversation(existingConversation.id);

      if (onMessagesRead) {
        onMessagesRead();
      }
    }
  }, [existingConversation?.id, existingConversation?.updated_at]);

  const loadMessagesForConversation = (conversationId) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoadingMessages(true);

    const now = Date.now();
    initialLoadTimestampRef.current = now;
    chatOpenedTimestampRef.current = now;

    getMessageForConversation(
      conversationId,
      1,
      (response) => {
        isLoadingRef.current = false;
        const loadedMessages = response.data.messages || [];
        setMessages(loadedMessages.reverse());
        setIsLoadingMessages(false);

        loadedMessages.forEach(msg => {
          seenMessageIds.current.add(msg.id);
        });

        const unreadIds = loadedMessages
          .filter(m => m.user_id !== user?.id)
          .map(m => m.id);

        if (unreadIds.length > 0) {
          markMessagesAsRead(
            conversationId,
            unreadIds,
            () => {},
            (error) => {}
          );
        }

        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
              behavior: "auto",
              block: "end"
            });
          }
        }, 300);
      },
      (error) => {
        isLoadingRef.current = false;
        setIsLoadingMessages(false);
      }
    );
  };

  const createConversationIfNeeded = () => {
    return new Promise((resolve, reject) => {
      if (conversationIdRef.current) {
        resolve(conversationIdRef.current);
        return;
      }

      if (isCreatingConversation) {
        reject(new Error("Already creating conversation"));
        return;
      }

      setIsCreatingConversation(true);

      const isConferenceCall = !!callInfo.conference_id;

      createCallChatConversation(
        callInfo.id,
        (response) => {
          const conversation = response.data.conversation;
          
          setCallChatConversation(conversation);
          conversationIdRef.current = conversation.id;
          setIsCreatingConversation(false);
          
          if (onConversationCreated) {
            onConversationCreated(conversation);
          }
          
          resolve(conversation.id);
        },
        (error) => {
          setIsCreatingConversation(false);
          
          if (error.response?.status === 409 || error.response?.status === 200) {
            const conversation = error.response.data.conversation;
            if (conversation) {
              setCallChatConversation(conversation);
              conversationIdRef.current = conversation.id;
              
              if (onConversationCreated) {
                onConversationCreated(conversation);
              }
              
              resolve(conversation.id);
              return;
            }
          }
          
          reject(error);
        },
        isConferenceCall
      );
    });
  };

  const handleShowFullScreenImg = (imageUrl, messageId, conversationId) => {
    setFullScreenImg({
      image: imageUrl,
      messageId: messageId,
      conversationId: conversationId,
    });
    setShowFullScreenImg(true);
  };

  const handleCloseFullScreenImg = () => {
    setShowFullScreenImg(false);
    setFullScreenImg(null);
  };

  const generateVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const video = document.createElement("video");
      video.setAttribute("playsinline", true);
      video.autoplay = true;
      video.muted = true;
      video.src = URL.createObjectURL(file);

      video.onloadeddata = () => {
        let ctx = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        video.pause();
        return resolve(canvas.toDataURL("image/png"));
      };
    });
  };

  function dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(","),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const handleSendMessage = async (messageText, parentMessageId = null) => {
    const trimmedText = messageText?.trim() || "";
    
    const hasMedia = mediaInput !== null;
    const hasFile = fileInput !== null;
    
    if (!trimmedText && !hasMedia && !hasFile) return;
    
    if (isSendingMessage) return;

    setIsSendingMessage(true);

    try {
      const conversationId = await createConversationIfNeeded();

      const formData = new FormData();

      formData.append("original_message", trimmedText);

      if (hasMedia) {
        const mediaType = mediaInput.type.split('/')[0];
        if (mediaType === "image") {
          formData.append("image", mediaInput);
        } else if (mediaType === "video") {
          formData.append("video", mediaInput);
          const thumbnail = await generateVideoThumbnail(mediaInput);
          const thumb = dataURLtoFile(thumbnail, "thumbnail");
          formData.append("video_thumbnail", thumb);
        }
      }

      if (hasFile) {
        formData.append("document", fileInput);
      }

      formData.append("device_sent_date", new Date().toISOString());

      if (isPanelViewer && !parentMessageId) {
        if (chatRecipient) {
          formData.append("conference_audience", CONFERENCE_CHAT_AUDIENCE_PRIVATE);
          formData.append("conference_recipient_user_id", chatRecipient.id);
        } else {
          formData.append("conference_audience", CONFERENCE_CHAT_AUDIENCE_PUBLIC);
        }
      }

      if (parentMessageId) {
        replyMessage(
          formData,
          conversationId,
          parentMessageId,
          (response) => {
            const newMessage = response.data.message;
            seenMessageIds.current.add(newMessage.id);
          
            setMessages((prev) => [...prev, newMessage]);
            setIsSendingMessage(false);
            
            setMediaInput(null);
            setFileInput(null);
            if (mediaInputFileRef.current) mediaInputFileRef.current.value = null;
            if (fileInputRef.current) fileInputRef.current.value = null;

            if (showReplyMessageModal) {
              handleCloseReply();
            }
            setTimeout(() => {
              messageInputRef.current?.focus();
            }, 0);
            setTimeout(scrollToBottom, 100);
          },
          (error) => {
            notifyError(t("general.something_wrong"));
            setIsSendingMessage(false);
          }
        );
      } else {
        addMessageForConversation(
          formData,
          conversationId,
          (response) => {
            const newMessage = response.data.message;
            seenMessageIds.current.add(newMessage.id);
          
            setMessages((prev) => [...prev, newMessage]);
            setIsSendingMessage(false);
            
            setMediaInput(null);
            setFileInput(null);
            if (mediaInputFileRef.current) mediaInputFileRef.current.value = null;
            if (fileInputRef.current) fileInputRef.current.value = null;
            setTimeout(() => {
              messageInputRef.current?.focus();
            }, 0);
            setTimeout(scrollToBottom, 100);
          },
          (error) => {
            notifyError(t("general.something_wrong"));
            setIsSendingMessage(false);
          }
        );
      }

    } catch (error) {
      notifyError(t("general.something_wrong"));
      setIsSendingMessage(false);
    }
  };

  const handleReactToMessage = (reaction, conversationId, messageId, callback) => {
    const reactionEmoji = mapEmoji(reaction);
    if (!reactionEmoji) return;

    reactToMessageForConversation(
      reactionEmoji,
      conversationId,
      messageId,
      (response) => {
        if (callback) callback();
      },
      (error) => {
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const handleRemoveOwnMessageReaction = (reactionId, conversationId, messageId, callback) => {
    removeOwnMessageReaction(
      conversationId,
      messageId,
      reactionId,
      (response) => {
        if (callback) callback();
      },
      (error) => {
        notifyError(t("general.something_wrong"));
      }
    );
  };

  const displayRightClickMenu = (e, messageId, conversationId, messageObject) => {
    e.preventDefault();
    e.stopPropagation();
    
    const selectedText = window.getSelection().toString().trim();
    if (selectedText) {
      return;
    }

    setSelectedMessageForContextMenu({
      messageId: messageId,
      conversationId: conversationId,
      messageObject: messageObject,
    });

    show({
      event: e,
    });
  };

  const handleCopyOption = (message) => {
    if (!message) return;
    
    let textToCopy = "";
    if (message.showOriginalMessage) {
      textToCopy = message.original_message || "";
    } else {
      const translated = findTranslatedTextForUser(
        message.message_translations,
        user
      );
      textToCopy = translated?.translation || message.original_message || "";
    }
    copy(textToCopy);
    notifySuccess(t('misc.copy'));
  };

  const handleDeleteMessage = (conversationId, messageId) => {
    deleteMessageForConversation(
      conversationId,
      messageId,
      (response) => {
        setMessages((prevMessages) => {
          return prevMessages.map((msg) => {
            if (msg.id === messageId) {
              return { ...msg, deleted: true, original_message: "" };
            }
            return msg;
          });
        });
      },
      (error) => {
        notifyError(t('general.something_wrong'));
      }
    );
  };

  const handleEditMessage = (newText) => {
    if (!messageToEdit || !newText.trim()) return;
    if (isSendingMessage) return;

    setIsSendingMessage(true);

    const formData = new FormData();
    formData.append("original_message", newText);

    editMessageForConversation(
      messageToEdit.conversation_id,
      messageToEdit.id,
      newText,
      (response) => {
        const updatedMessage = response.data.message;
        setMessages((prev) => {
          return prev.map((msg) => {
            if (msg.id === updatedMessage.id) {
              return updatedMessage;
            }
            return msg;
          });
        });
        
        setIsSendingMessage(false);
        handleCloseEdit();
      },
      (error) => {
        notifyError(t("general.something_wrong"));
        setIsSendingMessage(false);
      }
    );
  };

  const handleEditMessageOption = () => {
    if (!selectedMessageForContextMenu?.messageObject) return;

    if (showReplyMessageModal) {
      handleCloseReply();
    }
    
    setMessageToEdit(selectedMessageForContextMenu.messageObject);
    setShowEditMessageModal(true);
    
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 100);
  };

  const handleReplyMessageOption = () => {
    if (!selectedMessageForContextMenu?.messageObject) return;

    if (showEditMessageModal) {
      handleCloseEdit();
    }
    setMessageToReply(selectedMessageForContextMenu.messageObject);
    setShowReplyMessageModal(true);
    
    setTimeout(() => {
      if (messageInputRef.current) {
        messageInputRef.current.focus();
      }
    }, 100);
  };

  const handleCloseEdit = () => {
    setShowEditMessageModal(false);
    setMessageToEdit(null);
  };

  const handleCloseReply = () => {
    setShowReplyMessageModal(false);
    setMessageToReply(null);
  };

  useEffect(() => {
    if (!callChatConversation?.id || messages.length === 0) return;
    
    const unreadIds = messages
      .filter(m => m.user_id !== user?.id)
      .map(m => m.id);
    
    if (unreadIds.length > 0) {
      markMessagesAsRead(
        callChatConversation.id,
        unreadIds,
        () => {},
        (error) => {}
      );
    }
  }, [callChatConversation?.id]);

  useEffect(() => {
    leftPanelViewRef.current = leftPanelView;
  }, [leftPanelView]);

  useEffect(() => {
    notifyCallChatMessageRef.current = notifyCallChatMessage;
  }, [notifyCallChatMessage]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    if (!callChatConversation?.id || !user?.id) {
      return;
    }

    const messageObserver = {
      channelName: "MessagesChannel",
      update: (rawData) => {
        const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;

        const conversationId = data.conversation_id;
        if (conversationId !== callChatConversation.id) {
          return;
        }

        if (chatOpenedTimestampRef.current && data.created_at) {
          const messageCreatedAt = new Date(data.created_at).getTime();
          if (messageCreatedAt < chatOpenedTimestampRef.current) {
            setMessages((prev) => {
              return prev.map((msg) => {
                if (msg.id === data.id) {
                  return data;
                }
                return msg;
              });
            });
            
            seenMessageIds.current.add(data.id);
            return;
          }
        }

        if (seenMessageIds.current.has(data.id)) {
          setMessages((prev) => {
            return prev.map((msg) => {
              if (msg.id === data.id) {
                return data;
              }
              return msg;
            });
          });
          return;
        }
        
        seenMessageIds.current.add(data.id);

        setMessages((prev) => {
          if (prev.some(m => m.id === data.id)) {
            return prev;
          }
          return [...prev, data];
        });
        
        setTimeout(scrollToBottom, 100);
      },
    };

    add_observer(messageObserver);
    
    return () => {
      remove_observer(messageObserver);
    };
  }, [callChatConversation?.id, user?.id]);

  useEffect(() => {
    if (!callChatConversation?.id || messages.length === 0) return;
    
    const latestUnreadIds = messages
      .filter(m => m.user_id !== user?.id && !markedAsReadIds.current.has(m.id))
      .map(m => m.id);
    
    if (latestUnreadIds.length > 0) {
      latestUnreadIds.forEach(id => markedAsReadIds.current.add(id));
      
      markMessagesAsRead(
        callChatConversation.id,
        latestUnreadIds,
        (response) => {
          setConversations((prevConversations) => {
            return prevConversations.map((conv) => {
              if (conv.id === callChatConversation.id) {
                return {
                  ...conv,
                  unread_messages_ids: [],
                };
              }
              return conv;
            });
          });
          if (onMessagesRead) {
            onMessagesRead();
          }
        },
        (error) => {
          latestUnreadIds.forEach(id => markedAsReadIds.current.delete(id));
        }
      );
    }
  }, [messages.length, callChatConversation?.id, user?.id, onMessagesRead, setConversations]);

  return (
    <Container $fullWidth={fullWidth}>
      <CallChatHeader
        callInfo={callInfo}
        onClose={onClose}
        participantCount={callInfo?.other_participants?.length + 1 || 1}
      />

      <MessagesContainer>
        {(isCheckingConversation || isLoadingMessages) && (
          <LoadingWrapper>
            <SpinnerComponent isLoading={true} />
          </LoadingWrapper>
        )}

        {!isCheckingConversation && !isLoadingMessages && (!callChatConversation || messages.length === 0) && (
          <EmptyStateWrapper>
            <NoMessageIcon width={280} height={180} opacity={0.5} />
            <EmptyStateTitle>{t('chats.no_messages_yet')}</EmptyStateTitle>
            <EmptyStateText>{t('chats.start_a_conversation')}</EmptyStateText>
          </EmptyStateWrapper>
        )}

        {!isCheckingConversation && !isLoadingMessages && callChatConversation && messages.length > 0 && (
          <CallChatMessagesList
            messages={messages}
            callInfo={callInfo}
            isLoading={false}
            messagesEndRef={messagesEndRef}
            isPanelViewer={isPanelViewer}
            handleReactToMessage={handleReactToMessage}
            handleRemoveOwnMessageReaction={handleRemoveOwnMessageReaction}
            displayRightClickMenu={displayRightClickMenu}
            handleShowFullScreenImg={handleShowFullScreenImg}
          />
        )}
      </MessagesContainer>

      {showEditMessageModal && messageToEdit && (
        <EditMessageContainer
          message={messageToEdit}
          handleCloseEdit={handleCloseEdit}
        />
      )}

      {showReplyMessageModal && messageToReply && (
        <ReplyMessageContainer
          message={messageToReply}
          handleClose={handleCloseReply}
          user={user}
        />
      )}

      <CallChatInput
        onSendMessage={(text) => {
          if (showEditMessageModal && messageToEdit) {
            handleEditMessage(text);
          } else if (showReplyMessageModal && messageToReply) {
            handleSendMessage(text, messageToReply.id);
          } else {
            handleSendMessage(text);
          }
        }}
        disabled={isSendingMessage || isCreatingConversation}
        placeholder={
          showEditMessageModal 
            ? t("chat.edit_message") 
            : showReplyMessageModal 
            ? t("chat.reply") 
            : mediaInput || fileInput
              ? t("chat.add_caption")
              : t("chat.type_your_message")
        }
        mediaInputFileRef={mediaInputFileRef}
        fileInputRef={fileInputRef}
        setMediaInput={setMediaInput}
        setFileInput={setFileInput}
        messageInputRef={messageInputRef}
        initialValue={showEditMessageModal ? messageToEdit?.original_message : ""}
        mediaInput={mediaInput}
        fileInput={fileInput}
        showRecipientSelector={isPanelViewer && !showReplyMessageModal && !showEditMessageModal}
        recipientOptions={recipientOptions}
        selectedRecipient={chatRecipient}
        onSelectRecipient={setChatRecipient}
      />
      {callChatConversation && (
        <RightClickMenu
          handleDeleteMessage={handleDeleteMessage}
          handleReplyMessageOption={handleReplyMessageOption}
          handleEditMessageOption={handleEditMessageOption}
          handleCopyOption={handleCopyOption}
          contextMenuItem={selectedMessageForContextMenu}
          selectedConversation={callChatConversation}
          user={user}
          currentConversationMessages={messages}
          variant="call_chat"
          menuId={MENU_ID}
        />
      )}
      {showFullScreenImg && fullScreenImg && (
        <FullscreenImage
          image={fullScreenImg.image}
          handleCloseFullScreenImg={handleCloseFullScreenImg}
          handleDeletePhoto={() => {
            handleDeleteMessage(fullScreenImg.conversationId, fullScreenImg.messageId);
            setShowFullScreenImg(false);
          }}
          selectedConversation={callChatConversation}
        />
      )}
    </Container>
  );
};

export default CallChatPanel;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: ${({ $fullWidth }) => $fullWidth ? "0" : "300px"};
  max-width: ${({ $fullWidth }) => $fullWidth ? "none" : "400px"};
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  position: relative;
  height: 0;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 10px;
`;

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin-bottom: 10px;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1.5;
`;